import {
  MatterEventSchema,
  MatterNoteCommentSchema,
  MatterNoteSchema,
  MatterTaskCommentSchema,
  MatterTaskSchema,
} from "@/lib/data/schemas/matters/MatterSchema";
import { z } from "zod";

const massTortAnnotationFields = {
  matterId: z.string().nullable().optional(),
  massTortId: z.string(),
};

export const MASS_TORT_CUSTOM_FIELD_VALUE_TYPE_OPTIONS = [
  "select",
  "text",
  "date",
] as const;

export const MASS_TORT_TEMPLATE_GENERATION_EXTENSION_OPTIONS = [
  ".pdf",
  ".docx",
  ".doc",
] as const;

export const MASS_TORT_TEMPLATE_GENERATION_STATUS_OPTIONS = [
  "processing",
  "completed",
  "completed_with_errors",
] as const;

export const MassTortEventSchema = MatterEventSchema.extend(
  massTortAnnotationFields,
);
export const MassTortFileSchema = z
  .object({
    dateCreated: z.string(),
    dateModified: z.string(),
    description: z.string().nullable(),
    documentCategory: z.string().nullable(),
    firmId: z.string(),
    id: z.string(),
    idLocation: z.string().nullable(),
    isFolder: z.boolean(),
    massTortId: z.string(),
    matterId: z.string().nullable(),
    name: z.string(),
    pageCount: z.number().nullable(),
    presignedUrlKey: z.string().nullable(),
    tags: z.array(z.string()).nullable(),
    variables: z.array(z.string()).nullable(),
  })
  .passthrough();
export const MassTortTaskSchema = MatterTaskSchema.extend(
  massTortAnnotationFields,
);
export const MassTortTaskCommentSchema = MatterTaskCommentSchema.extend(
  massTortAnnotationFields,
);
export const MassTortNoteSchema = MatterNoteSchema.extend(
  massTortAnnotationFields,
);
export const MassTortNoteCommentSchema = MatterNoteCommentSchema.extend(
  massTortAnnotationFields,
);

export const MassTortCustomFieldSchema = z.object({
  dateCreated: z.string(),
  dateModified: z.string(),
  description: z.string().nullable(),
  firmId: z.string(),
  id: z.string(),
  label: z.string(),
  massTortId: z.string(),
  order: z.number(),
  valueOptions: z.array(z.string()).nullable(),
  valueType: z.enum(MASS_TORT_CUSTOM_FIELD_VALUE_TYPE_OPTIONS),
});

export const MassTortMatterCustomFieldSchema = z.object({
  dateCreated: z.string(),
  dateModified: z.string(),
  firmId: z.string(),
  id: z.string(),
  massTortCustomFieldId: z.string(),
  massTortId: z.string(),
  matterId: z.string(),
  value: z.string().nullable(),
});

const MassTortGeneratedFileTemplateRequestFailedJobSchema = z
  .object({
    error: z.string(),
    matterId: z.string(),
    missingVariables: z.array(z.string()).optional(),
  })
  .passthrough();

export const MassTortGeneratedFileTemplateRequestSchema = z.object({
  completedJobs: z.number(),
  dateCreated: z.string().nullable().optional(),
  dateModified: z.string().nullable().optional(),
  extension: z.enum(MASS_TORT_TEMPLATE_GENERATION_EXTENSION_OPTIONS),
  failedJobDetails: z
    .array(MassTortGeneratedFileTemplateRequestFailedJobSchema)
    .nullable()
    .optional(),
  failedJobs: z.number(),
  fileId: z.string().nullable().optional(),
  firmId: z.string(),
  id: z.string(),
  idLocation: z.string().nullable().optional(),
  massTortId: z.string(),
  name: z.string(),
  processedMatterIds: z.array(z.string()).nullable().optional(),
  requestedByUsername: z.string().nullable().optional(),
  sourceFileKey: z.string().nullable().optional(),
  status: z.enum(MASS_TORT_TEMPLATE_GENERATION_STATUS_OPTIONS),
  templateId: z.string().nullable().optional(),
  templateInputs: z.record(z.unknown()).nullable().optional(),
  totalJobs: z.number(),
});

export const MassTortTemplateGenerationRetryResultSchema = z.object({
  requests: z.array(MassTortGeneratedFileTemplateRequestSchema),
  retriedJobCount: z.number(),
});

export const MassTortSchema = z
  .object({
    dateCreated: z.string().nullable().optional(),
    dateModified: z.string().nullable().optional(),
    description: z.string().nullable(),
    firmId: z.string(),
    id: z.string(),
    title: z.string().nullable().optional(),
    customFields: MassTortCustomFieldSchema.array(),
    eventLogs: MassTortEventSchema.array(),
    files: MassTortFileSchema.array(),
    noteComments: MassTortNoteCommentSchema.array(),
    notes: MassTortNoteSchema.array(),
    taskComments: MassTortTaskCommentSchema.array(),
    tasks: MassTortTaskSchema.array(),
  })
  .passthrough();

export const MassTortSearchSchema = z
  .object({
    dateCreated: z.string().nullable().optional(),
    dateModified: z.string().nullable().optional(),
    description: z.string().nullable(),
    firmId: z.string(),
    id: z.string(),
    title: z.string().nullable().optional(),
  })
  .passthrough();

export const MassTortMatterSearchSchema = z
  .object({
    dateCreated: z.string().nullable().optional(),
    dateModified: z.string().nullable().optional(),
    assignees: z.array(z.string()).default([]),
    clientEmails: z
      .array(
        z
          .object({
            address: z.string(),
            description: z.string().nullable(),
            id: z.string(),
            isPrimary: z.boolean(),
            label: z.string().nullable(),
          })
          .passthrough(),
      )
      .default([]),
    clientName: z.string().nullable().optional(),
    clientPhones: z
      .array(
        z
          .object({
            description: z.string().nullable(),
            extension: z.string().nullable(),
            id: z.string(),
            isPrimary: z.boolean(),
            label: z.string().nullable(),
            number: z.string(),
          })
          .passthrough(),
      )
      .default([]),
    dateOfLoss: z.string().nullable().optional(),
    defendantNames: z.array(z.string()).default([]),
    emailAddress: z.string().nullable().optional(),
    firmId: z.string(),
    id: z.string(),
    incidentAddressCity: z.string().nullable().optional(),
    incidentAddressState: z.string().nullable().optional(),
    insuranceClaimNumbers: z.array(z.string()).default([]),
    insurancePolicyNumbers: z.array(z.string()).default([]),
    massTortId: z.string(),
    statuteOfLimitations: z
      .array(
        z
          .object({
            confirmed: z.boolean().optional(),
            date: z.string(),
            description: z.string().nullable(),
            id: z.string(),
          })
          .passthrough(),
      )
      .default([]),
    statusCategory: z.string().nullable().optional(),
    statusLabel: z.string().nullable().optional(),
    tags: z.array(z.string()).default([]),
    teams: z.array(z.string()).default([]),
    title: z.string().nullable().optional(),
    incidentType: z.string().nullable().optional(),
    claimTypeIds: z.array(z.string()).default([]),
    claimTypes: z
      .array(z.object({ id: z.string(), label: z.string() }))
      .default([]),
  })
  .passthrough();
